import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardText,
  CardHeader,
  Button,
  Alert
} from 'reactstrap'

import Modal from 'ui/modal'
import CalendarPicker from 'components/calendar_picker'
import ProductCard from 'containers/list/products/product_card'
import ApiFetcher from 'lib/api_fetcher'
import time from 'i18n/time'
import { useTranslation } from 'react-i18next'

const AVAILABILITY_OPTIONS = [
  { code: 'orsag1', label: 'Sagrada Familia' },
]

const AvailabilityModal = ({
  availabilityGroupCode,
  modal,
  toggle,
  urls
}) => {
  const {i18n} = useTranslation()
  const [isFetching, setIsFetching] = useState(false)
  const [isFetchingProducts, setIsFetchingProducts] = useState(false)

  const [response, setResponse] = useState(null)
  const [productsResponse, setProductsResponse] = useState(null)

  const [error, setError] = useState(null)
  const [productsError, setProductsError] = useState(null)

  const [activeMonth, setActiveMonth] = useState(
    time().startOf('month').format('YYYY-MM-DD')
  )

  const endpoint = useMemo(() => {
    if (!availabilityGroupCode) return null

    return urls.availability_group.replace(
      / *\:[^)]*\: */g,
      availabilityGroupCode
    )
  }, [urls.availability_group, availabilityGroupCode])

  const productsEndpoint = useMemo(() => {
    return urls.products_grid
  }, [urls.products_grid])

  const daysAvailable = useMemo(() => {
    return Object.keys(response?.availability || {})
  }, [response])

  const fetchAvailability = useCallback((date = activeMonth) => {
    if (!endpoint) return Promise.resolve()

    setIsFetching(true)
    setResponse(null)
    setError(null)
    setProductsResponse(null)
    setProductsError(null)

    PubSub.publish('loading-page')

    const startDate = time(date).startOf('month').format('YYYY-MM-DD')
    const endDate = time(date).endOf('month').format('YYYY-MM-DD')

    return new ApiFetcher({
      method: 'GET',
      key: 'availability_group',
      endpoint,
      query: {
        start_date: startDate,
        end_date: endDate
      }
    }).call()
      .then((res) => {
        if (res?.success === false) {
          setError(res?.message || i18n.t('validations:availability_fetch_error'))
          return
        }

        setResponse(res)
      })
      .catch(() => {
        setError(i18n.t('validations:fetch_error'))
      })
      .finally(() => {
        PubSub.publish('loaded-page')
        setIsFetching(false)
      })
  }, [endpoint, activeMonth])

  const fetchProducts = useCallback((productIds) => {
    if (!productsEndpoint || !productIds?.length) {
      return Promise.resolve()
    }

    setIsFetchingProducts(true)
    setProductsResponse(null)
    setProductsError(null)

    PubSub.publish('loading-page')

    return new ApiFetcher({
      method: 'GET',
      key: 'products_grid',
      endpoint: productsEndpoint,
      query: {
        'ids[]': productIds
      }
    }).call()
      .then((res) => {
        if (res?.success === false) {
          setProductsError(res?.message || i18n.t('validations:products_fetch_error'))
          return
        }

        setProductsResponse(res)
      })
      .catch(() => {
        setProductsError(i18n.t('validations:fetch_error'))
      })
      .finally(() => {
        PubSub.publish('loaded-page')
        setIsFetchingProducts(false)
      })
  }, [productsEndpoint])

  const handleDaySelected = useCallback((day) => {
    const selectedDate = time(day)
      .tz('Europe/Madrid')
      .format('YYYY-MM-DD')

    const productIds = response?.availability?.[selectedDate] || []

    fetchProducts(productIds)
  }, [response, fetchProducts])

  const handleMonthChange = useCallback((date) => {
    const newActiveMonth = time(date)
      .startOf('month')
      .format('YYYY-MM-DD')

    setActiveMonth(newActiveMonth)
    fetchAvailability(newActiveMonth)
  }, [fetchAvailability])

  useEffect(() => {
    if (!modal || !endpoint) return

    fetchAvailability(activeMonth)
  }, [modal, endpoint])

  return (
    <Modal
      header="Sagrada Familia"
      isOpen={modal}
      toggle={toggle}
    >
      {isFetching && (
        <div className="mt-3 alert alert-warning">
          {i18n.t('availability_group_widget.fetching_availability')}
        </div>
      )}

      {error && (
        <Alert color="danger">
          {error}
        </Alert>
      )}

      {response?.success === true && !error && (
        <CalendarPicker
          key={activeMonth}
          initialDay={activeMonth}
          onDaySelected={handleDaySelected}
          onMonthChange={handleMonthChange}
          daysAvailable={daysAvailable}
        />
      )}

      {isFetchingProducts && (
        <div className="mt-3 alert alert-warning">
          {i18n.t('availability_group_widget.fetching_products')}
        </div>
      )}

      {productsError && (
        <Alert color="danger" className="mt-3">
          {productsError}
        </Alert>
      )}

      {productsResponse?.pagination?.total_items >= 1 && (
        <div className="mt-5">
          <div className="h5 p-0 m-0">
            {i18n.t('availability_group_widget.activities_available_count', {count: productsResponse.pagination.total_items})}
          </div>
          <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-3 p-0 mt-2">
            {productsResponse.products.map((product, key) => (
              <ProductCard
                {...product}
                key={key}
                urls={urls}
                url={urls.service.replace(/ *\:[^)]*\: */g, product.slug)}
              />
            ))}
          </div>
        </div>
      )}

      {productsResponse?.pagination?.total_items == 0 && (
        <div className="mt-3 alert alert-warning">
          {i18n.t('availability_group_widget.no_products_available')}
        </div>
      )}
    </Modal>
  )
}

const AvailabilityGroupWidget = (props) => {
  const [availabilityGroupCode, setAvailabilityGroupCode] = useState(null)
  const [modal, setModal] = useState(false)
  const {i18n} = useTranslation()

  const closeModal = useCallback(() => {
    setModal(false)
  }, [])

  const openModal = useCallback((code) => {
    setAvailabilityGroupCode(code)
    setModal(true)
  }, [])

  return (
    <Card className="border-danger mb-3 shadow">
      <CardHeader className="bg-danger text-white">
        <span className="fw-bold">
          <i className="fa fa-calendar me-2" />
          {i18n.t('availability_group_widget.widget_header')}
        </span>
      </CardHeader>

      <CardBody>
        <CardTitle tag="h5">
          Sagrada Familia
        </CardTitle>

        <CardText>
          {i18n.t('availability_group_widget.widget_description')}
        </CardText>

        <Row>
          {AVAILABILITY_OPTIONS.map((option) => (
            <Col key={option.code}>
              <Button
                color="danger"
                className="w-100 fw-bold"
                onClick={() => openModal(option.code)}
              >
                {i18n.t('availability_group_widget.availability_button')}
              </Button>
            </Col>
          ))}
        </Row>
      </CardBody>

      <AvailabilityModal
        {...props}
        modal={modal}
        toggle={closeModal}
        availabilityGroupCode={availabilityGroupCode}
      />
    </Card>
  )
}

export default AvailabilityGroupWidget
